import React from 'react';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';

export const TIME_TRACKER_HELP_GUIDE_ID = 'time-tracker-help';
const ACCENT = '#0d9488';

const DISCIPLINES = [
  { id: 'PIP', name: 'Piping', labour: '21 000', extra: '81 000', color: '#0d6efd' },
  { id: 'MWR', name: 'M&R', labour: '22 000', extra: '82 000', color: '#6f42c1' },
  { id: 'ELE', name: 'Electrical', labour: '23 000', extra: '83 000', color: '#fd7e14' },
  { id: 'SHM', name: 'Sheet Metal', labour: '24 000', extra: '84 000', color: '#20c997' },
  { id: 'PFS', name: 'Pipe Shop', labour: '25 000', extra: '85 000', color: '#e83e8c' },
  { id: 'CIV', name: 'Civil', labour: '26 000', extra: '86 000', color: '#795548' },
  { id: 'STR', name: 'Structural', labour: '27 000', extra: '87 000', color: '#607D8B' },
  { id: 'TNK', name: 'Tank Fab', labour: '28 000', extra: '88 000', color: '#dc3545' },
  { id: 'BMT', name: 'Boilermaker', labour: '29 000', extra: '89 000', color: '#17a2b8' },
];

const FlowStep = ({ n, icon, title, body, tone = 'teal' }) => {
  const tones = {
    teal: { bg: '#f0fdfa', border: '#99f6e4', num: ACCENT },
    blue: { bg: '#eff6ff', border: '#bfdbfe', num: '#2563eb' },
    amber: { bg: '#fffbeb', border: '#fde68a', num: '#d97706' },
    slate: { bg: '#f8fafc', border: '#e2e8f0', num: '#475569' },
  };
  const t = tones[tone] || tones.teal;
  return (
    <div style={{
      flex: '1 1 130px', minWidth: 118, background: t.bg, border: `1px solid ${t.border}`,
      borderRadius: 12, padding: '14px 12px 16px', textAlign: 'center',
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: '50%', background: t.num, color: '#fff',
        fontWeight: 800, fontSize: 13, display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', marginBottom: 8,
      }}>{n}</div>
      <div style={{ fontSize: 20, lineHeight: 1, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>{body}</div>
    </div>
  );
};

const Arrow = () => (
  <div style={{
    flex: '0 0 auto', alignSelf: 'center', color: '#94a3b8',
    fontSize: 20, fontWeight: 800, padding: '0 2px',
  }} aria-hidden>→</div>
);

const SectionLabel = ({ children }) => (
  <h4 style={{
    margin: '0 0 10px', fontSize: 12, fontWeight: 800, color: '#334155',
    textTransform: 'uppercase', letterSpacing: 0.6,
  }}>
    {children}
  </h4>
);

/**
 * Graphical Time Tracker help — labour / op-code process + how to enter hours.
 */
const TimeTrackerHelpModal = ({ onClose }) => {
  if (!onClose) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 11000, padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 820,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 60px rgba(15,23,42,0.32)', overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 20px',
          background: `linear-gradient(135deg, ${ACCENT} 0%, #0f766e 55%, #115e59 100%)`,
          color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, opacity: 0.85, textTransform: 'uppercase' }}>
              Time Tracker
            </div>
            <h3 style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 800 }}>Hours, labour codes, and extras</h3>
            <div style={{ fontSize: 12, opacity: 0.9, marginTop: 3 }}>
              Where codes come from · how you pick them · what happens after save
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ListenButton compact guideId={TIME_TRACKER_HELP_GUIDE_ID} title="Time Tracker Help" accent={ACCENT} dark />
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff',
                borderRadius: 8, width: 34, height: 34, cursor: 'pointer', fontSize: 20, lineHeight: 1,
              }}
              aria-label="Close"
            >×</button>
          </div>
        </div>

        <div style={{ padding: '18px 20px 22px', overflowY: 'auto', flex: 1 }}>
          <HelpWithVideoTour
            guideId={TIME_TRACKER_HELP_GUIDE_ID}
            title="Time Tracker Help"
            accent={ACCENT}
            interactiveVariant="field"
            onStartInteractive={() => {
              onClose();
              window.dispatchEvent(new CustomEvent('tt:startInteractiveTour', { detail: { variant: 'field' } }));
            }}
          >
            <section style={{ marginBottom: 22 }}>
              <SectionLabel>The rule: job plus discipline</SectionLabel>
              <div style={{
                padding: '14px 16px', borderRadius: 12,
                background: 'linear-gradient(180deg, #ecfdf5 0%, #fff 70%)',
                border: '1px solid #6ee7b7',
              }}>
                <p style={{ margin: 0, fontSize: 13, color: '#065f46', lineHeight: 1.5, fontWeight: 600 }}>
                  Every Time Tracker list is a job plus a discipline. You always pick the project, then PIP, MWR, ELE, and so on.
                </p>
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                  A job appears when it has that discipline on the project, or when the job number already ends in the three-letter code
                  (30437PIP is Piping). Standard codes, Change Log extras, and the timesheet dropdown all use that same pair.
                  If you do not open the pair, the extra is not missing — you have not opened its list yet.
                </p>
              </div>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>The labour-code path</SectionLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 6 }}>
                <FlowStep
                  n="1"
                  icon="📘"
                  title="Maestro catalogue"
                  body="Five-digit labour codes live in Accounting → Coding Structure."
                  tone="slate"
                />
                <Arrow />
                <FlowStep
                  n="2"
                  icon="⚙️"
                  title="Admin loads the job"
                  body="Time Tracker Admin → Op Codes (or Extras) for that project + discipline."
                  tone="blue"
                />
                <Arrow />
                <FlowStep
                  n="3"
                  icon="🔎"
                  title="Timesheet dropdown"
                  body="Daily or Weekly shows only active codes for the job you picked."
                  tone="teal"
                />
                <Arrow />
                <FlowStep
                  n="4"
                  icon="⏱️"
                  title="Hours save"
                  body="Description stamps onto the row. Payroll and actuals read that code."
                  tone="amber"
                />
              </div>
              <p style={{
                margin: '12px 0 0', fontSize: 12, color: '#475569', lineHeight: 1.5,
              }}>
                The timesheet does <strong>not</strong> invent codes from Maestro. If the dropdown is empty,
                Accounting has not loaded that project-discipline yet.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>What counts as hours</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 12, padding: '12px 14px' }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>In the total</div>
                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.45, marginTop: 4 }}>
                    Regular, time and a half, and double. Overtime is typed. The sheet does not calculate it.
                    A person cannot go over 24 hours on one day.
                  </div>
                </div>
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '12px 14px' }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>Not in the total</div>
                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.45, marginTop: 4 }}>
                    Height, room and board, meals, travel dollars, and bus. Enter them once per person per day.
                    They split across that day’s rows by hours. Pin a row to keep an exact amount.
                  </div>
                </div>
              </div>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>How you enter time</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{
                  background: 'linear-gradient(180deg, #f0fdfa 0%, #fff 70%)',
                  border: '1px solid #99f6e4', borderRadius: 12, padding: '14px 16px',
                }}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>📅</div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>Daily</div>
                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.45, marginTop: 4 }}>
                    Crew heads enter one day for the whole crew. One row per person, project, and labour code.
                    There is no draft. Saving writes the rows as submitted.
                    The plus beside the employee copies that person onto a new row with no operation code.
                    The plus beside the operation code copies that code onto a new row with no employee.
                    Room and board, meals, and travel are typed once per person under Daily Allowances. Calculate previews the split. Submit writes it.
                  </div>
                </div>
                <div style={{
                  background: 'linear-gradient(180deg, #eff6ff 0%, #fff 70%)',
                  border: '1px solid #bfdbfe', borderRadius: 12, padding: '14px 16px',
                }}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>🗓️</div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>Weekly</div>
                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.45, marginTop: 4 }}>
                    One person, Sunday–Saturday (Canada) or Monday–Sunday (US). The week follows that person’s payroll group.
                    The grid auto-saves as draft until you press Submit.
                    This button is for project managers, accounting, and superadmin.
                    Foremen, general foremen, and superintendents enter the crew on Daily.
                  </div>
                </div>
              </div>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>Calculate is a preview</SectionLabel>
              <p style={{ margin: '0 0 10px', fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                On Daily, type that person’s room and board, meals, and travel total in the Daily Allowances boxes under the grid.
                Calculate splits those dollars across the rows that already have a project, an operation code, and hours.
                The split follows regular hours plus time and a half plus double time. Height is left out. Calculate does not save.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 14px' }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>How the split lands</div>
                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5, marginTop: 6 }}>
                    One row with 8 hours gets the whole amount. Two rows, 6 hours and 2 hours, split a $100 room and board into $75 and $25.
                    Cents that do not divide evenly land on the row with the most hours.
                    The preview shows as a grey number in the empty cells, and a green calculated tag appears beside the name.
                  </div>
                </div>
                <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 12, padding: '12px 14px' }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>A typed amount stays put</div>
                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5, marginTop: 6 }}>
                    A number you type in a row cell is pinned to that row. Calculate leaves it alone and splits only what is left across the blank rows.
                    Submit for Review runs the same split again and writes the dollars onto the saved rows.
                    You can submit without clicking Calculate.
                  </div>
                </div>
              </div>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>What the numbers mean</SectionLabel>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))', gap: 8,
              }}>
                {DISCIPLINES.map((d) => (
                  <div key={d.id} style={{
                    border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 10px 12px',
                    background: '#fff',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%', background: d.color, flexShrink: 0,
                      }} />
                      <strong style={{ fontSize: 12, color: d.color }}>{d.id}</strong>
                      <span style={{ fontSize: 11, color: '#64748b' }}>{d.name}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#334155', fontFamily: 'ui-monospace, Consolas, monospace' }}>
                      Labour {d.labour}
                    </div>
                    <div style={{ fontSize: 11, color: '#92400e', fontFamily: 'ui-monospace, Consolas, monospace' }}>
                      Extra {d.extra}
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ margin: '10px 0 0', fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
                Extra work uses the same discipline plus sixty thousand. Indirects (PM, QC, safety, room and board)
                sit in the zero-one-thousand range. Full catalogue: Accounting → Coding Structure.
              </p>
            </section>

            <section style={{ marginBottom: 8 }}>
              <SectionLabel>If a code is missing</SectionLabel>
              <div style={{ display: 'grid', gap: 8 }}>
                {[
                  ['A', '#ccfbf1', ACCENT, 'Admin → Op Codes', 'Pick the project and discipline, then add or Excel-import the five-digit codes. They must be numeric and at most five digits.'],
                  ['B', '#fef3c7', '#d97706', 'Admin → Extras', 'Change-order / extra codes for that same project-discipline. They show in the dropdown with an amber Extra badge.'],
                  ['C', '#e2e8f0', '#475569', 'Green vs gray dot', 'Inactive codes stay on old timesheets but hide from the dropdown. Click the status dot to bring one back.'],
                ].map(([letter, chip, color, title, body]) => (
                  <div key={letter} style={{
                    display: 'flex', gap: 12, padding: '12px 14px',
                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                  }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: 8, background: chip, color,
                      fontWeight: 800, fontSize: 14, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', flexShrink: 0,
                    }}>{letter}</div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>{title}</div>
                      <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.45, marginTop: 2 }}>{body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>Draft, submit, and who can change a row</SectionLabel>
              <div style={{ display: 'grid', gap: 8 }}>
                {[
                  ['1', '#ccfbf1', ACCENT, 'Weekly is a draft until Submit', 'PDF reports read submitted rows. A draft stays off the PDF. There is no approval queue after Submit.'],
                  ['2', '#dbeafe', '#2563eb', 'Daily is submitted immediately', 'A crew day does not sit in draft. Saving is the submit.'],
                  ['3', '#fef3c7', '#d97706', 'Your own drafts, while the week is open', 'After submit, a manager corrects the row. A locked week blocks field edits.'],
                ].map(([letter, chip, color, title, body]) => (
                  <div key={letter} style={{
                    display: 'flex', gap: 12, padding: '12px 14px',
                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                  }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: 8, background: chip, color,
                      fontWeight: 800, fontSize: 14, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', flexShrink: 0,
                    }}>{letter}</div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>{title}</div>
                      <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.45, marginTop: 2 }}>{body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>Who sees the hours</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ fontWeight: 800, fontSize: 13 }}>Your own, or your crew</div>
                  <div style={{ fontSize: 12, color: '#475569', marginTop: 4, lineHeight: 1.4 }}>Everyone sees search, who is viewing, Help, Daily, Crews, and Analytics. Weekly is for project managers, accounting, and superadmin. Analytics is your hours, plus your crew if you head one.</div>
                </div>
                <div style={{ border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 12px', background: '#eff6ff' }}>
                  <div style={{ fontWeight: 800, fontSize: 13 }}>A project team</div>
                  <div style={{ fontSize: 12, color: '#475569', marginTop: 4, lineHeight: 1.4 }}>Foremen and project managers also see the people on their team.</div>
                </div>
                <div style={{ border: '1px solid #99f6e4', borderRadius: 10, padding: '10px 12px', background: '#f0fdfa' }}>
                  <div style={{ fontWeight: 800, fontSize: 13 }}>Every job</div>
                  <div style={{ fontSize: 12, color: '#475569', marginTop: 4, lineHeight: 1.4 }}>Accounting and superadmin. Admin and Audit stay with them. Payroll sees hours and Audit, not the Admin gear.</div>
                </div>
              </div>
              <p style={{ margin: '10px 0 0', fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
                The phone page uses the same Daily save. Weekly on the phone is the same roles as the Weekly button here.
              </p>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>Two ways hours reach payroll</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 12, padding: '12px 14px' }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>The job has a clerk</div>
                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5, marginTop: 6 }}>
                    The foreman enters the crew’s hours. The clerk reviews them on this log and confirms they are right.
                    Then the payroll clerk, for example Faith, reviews them and enters them in Maestro.
                  </div>
                </div>
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '12px 14px' }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>The job has no clerk</div>
                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5, marginTop: 6 }}>
                    When the foreman is done, the hours go straight to the payroll clerk. There is no clerk review in between.
                  </div>
                </div>
              </div>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>Your hours stay</SectionLabel>
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>A removed row is hidden</div>
                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.45, marginTop: 4 }}>
                    Timesheets, codes, extras, crews, and payroll weeks are marked removed. The row stays.
                    Daily and Weekly saves write the audit row with the hours, including room and board, meals, and travel.
                    If that audit write fails, the hours are not kept. Accounting brings a removed row back from Audit. An employee is turned off, not wiped.
                  </div>
                </div>
                <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>Azure holds the database</div>
                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.45, marginTop: 4 }}>
                    The hours and the audit log live in the same Azure database. Azure can restore that whole database to an earlier moment.
                    A separate copy of these Time Tracker tables can be kept outside Azure, so the hours are not only inside that one database.
                    That copy is not a button on this page. Git keeps the program. It does not keep the hours.
                  </div>
                </div>
              </div>
            </section>

            <section style={{ marginBottom: 22 }}>
              <SectionLabel>A locked week is the payroll group</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                Canada and the United States are locked separately. Locking Canada freezes that week for every Canadian employee, on every job.
                The payroll group comes from the division name. A name that ends in (US) is the United States. A name that ends in (CA) is Canada.
                If the division does not say the country, or the person has no division, there is no payroll group and the week lock does not cover them.
                Do not lock a week that people are still entering.
              </p>
            </section>

            <section style={{ marginBottom: 8 }}>
              <SectionLabel>The PDF and the Maestro file are different</SectionLabel>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                PDF reports use submitted rows only. The Maestro payroll file includes draft and submitted hours that have not been removed.
                A line with zero hours is left out. Removed rows stay out. The presentation job PRESO-TT-001 stays out.
                Submit drafts before export, or the file will contain hours the PDF does not show.
              </p>
            </section>

            <p style={{
              margin: '16px 0 0', padding: '10px 12px', borderRadius: 8,
              background: '#ecfdf5', border: '1px solid #a7f3d0',
              fontSize: 12, color: '#065f46', lineHeight: 1.45,
            }}>
              Tip: pick the <strong>project-discipline first</strong> (for example 99999 PFS), then search the labour code.
              The description list opens wide enough to read the full name. Switching project clears the code so you never charge piping hours to the pipe shop list.
            </p>
          </HelpWithVideoTour>
        </div>
      </div>
    </div>
  );
};

export default TimeTrackerHelpModal;
